import React, {lazy, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useFormik} from "formik";
import * as yup from "yup";
import {hashString} from "utils";
import {Link, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import authActions from "../../redux/modules/auth/authActions";
import "react-simple-keyboard/build/css/index.css";
import {selectAuthLoading} from "../../redux/modules/auth/authSelector";
import AUTH_ROUTES from "routes/auth.routes";
import {useAlert} from "hooks/use-alert";
import {useDisclosure} from "hooks/use-disclosure";
import getDeviceInfo from "helper/getDeviceInfo";
import {useAppState} from "hooks/use-app-state";
import {useQuery} from "hooks/use-query";
import {isActionSuccess} from "../../redux/utils";
import {FaTimes} from "react-icons/fa";
import {AuthImage} from "components/AuthImage";
import {AppLogo} from "components/AppLogo";

const Button = lazy(() => import("components/Base/Button"));
const Input = lazy(() => import("components/Base/Input"));
const LanguageSelector = lazy(() => import("components/LanguageSelector"));
const InitializePasswordModal = lazy(() =>
    import("../../components/Auth/InitializePasswordModal")
);
const LinkAccModal = lazy(() =>
    import("../../components/Auth/LinkAccountModal")
);
const ChangePinModal = lazy(() =>
    import("../../components/Auth/ChangePinModal")
);
const OtpModal = lazy(() => import("components/OtpModal"));
const Keyboard = lazy(() => import("react-simple-keyboard"));

export const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d!@#$%^&*()-_=+{};:'",.<>?/`~]{8,20}$/;

const LoginScreen = () => {
    const keyboardRef = React.useRef(null);
    const [processing, setProcessing] = useState(false);
    const [showKeyboard, setShowKeyboard] = useState(true);

    const query = useQuery();

    const [state, setState] = useState({
        checked: true,
    });
    const dispatch = useDispatch();
    const loading = useSelector(selectAuthLoading);
    const {t} = useTranslation();
    const {triggerAlert} = useAlert();
    const [show, setShow] = useDisclosure();
    const [showPin, setShowPin] = useDisclosure();
    const [otpState, setOtpState] = useAppState();
    const [otpModal, setOtpModal] = useDisclosure();
    const [deviceInfo, setDeviceInfo] = useState({});
    const [initLoading, setInitLoadin] = useState(false);
    const [initPinLoading, setInitPinLoadin] = useState(false);
    const [headerText, setHeaderText] = useState();
    const [showLinkAcc, setShowLinkAcc] = useDisclosure();
    const [errorToDisplay, setErrorToDisplay] = useState("");
    const [userData, setUserData] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const info = getDeviceInfo();
        setDeviceInfo(info);
        const sessionExpired = query.get("sessionExpired") === "true";
        if (sessionExpired) {
            setErrorToDisplay("Session expired, Please login again");
            query.set("sessionExpired", false);
        }
    }, []);

    const initialValues = {
        pwd: "",
        codewal: "",
    };

    const validationSchema = yup.object({
        codewal: yup.string().required("Required Field"),
        pwd: yup.string().required("Required Field"),
        newPassword: yup
            .string()
            .matches(passwordRegex, t("login.password_check"))
            .min(8, t("Settings.password_min_length"))
            .max(15, t("Settings.password_max_length")),
        cnewPassword: yup
            .string()
            .oneOf([yup.ref("newPassword"), null], t("login.match_password")),
        oldPassword: yup.string(),
        newpin: yup
            .string()
            .min(
                userData?.pinLength ? parseInt(userData?.pinLength, 10) ?? 4 : 4,
                `${t("Settings.new_password_min_length") + userData.pinLength}`
            )
            .max(
                userData?.pinLength ? parseInt(userData?.pinLength, 10) ?? 4 : 4,
                `${t("Settings.new_password_min_length") + userData.pinLength}`
            ),
        oldpin: yup.string(),
        cnewpin: yup
            .string()
            .oneOf([yup.ref("newpin"), null], t("login.match_pin")),
    });

    const loginStatusMesages = {
        "01": "",
        "04": "login.account_inactif",
        "08": "login.not_sub",
        "02": "login.account_locked",
        11: "login.acc_attach_pen",
        13: "login.acc_attach",
        14: "login.not_active_sub",
        102: "login.attempts_exceeded",
        115: "login.pwd_expired",
        116: "login.pin_expired",
        103: "login.wait",
    };

    const onKeyPress = (button) => {
        if (button === "{shift}" || button === "{lock}") handleShift();
    };

    const handleShift = () => {
        const layoutName = state.layoutName;
        setState({
            ...state,
            layoutName: layoutName === "default" ? "shift" : "default",
        });
    };

    const handleSubmit = (values) => {
        setShowKeyboard(true);
        setErrorToDisplay("");

        let data = {
            ...values,
            pwd: hashString(values.pwd),
            etab: "001",
            type: "004",
            product: "Web-Banking",
            platform: "web",
            deviceInfo,
        };

        dispatch(authActions.handleLogin(data)).then((action) => {
            try {
                let res = action.payload;

                if (
                    res?.success !== "01" &&
                    res?.success !== "03" &&
                    res?.success !== "10"
                ) {
                    setErrorToDisplay(
                        loginStatusMesages[res.success]
                            ? t(loginStatusMesages[res.success])
                            : res.message || "Please verify your credentials"
                    );
                } else {
                    setErrorToDisplay("");
                }

                if (!res) {
                    setErrorToDisplay("Error occurred on the server unable to login.");
                    return;
                }

                setUserData(res);
                localStorage.setItem("userData", res);

                if (res.success === "01") {
                    if (res.isOTPRequired) {
                        setOtpModal.open();
                    } else {
                        triggerAlert(
                            "success",
                            "Login successful",
                            "You were succesfully logged in."
                        );
                        navigate("/overview");
                    }
                } else if (res.success === "03") {
                    setShow.open();
                    localStorage.setItem("token", res.token);
                } else if (res.success === "115") {
                    formik.setFieldValue("oldPassword", "");
                    formik.setFieldValue("cnewPassword", "");
                    formik.setFieldValue("newPassword", "");
                    setShow.open();
                    setHeaderText(t("login.pwd_expired"));
                    localStorage.setItem("token", res.token);
                } else if (res.success === "10") {
                    formik.setFieldValue("oldpin", "");
                    formik.setFieldValue("newpin", "");
                    formik.setFieldValue("cnewpin", "");

                    setShowPin.open();
                    localStorage.setItem("token", res.token);
                } else if (res.success === "116") {
                    formik.setFieldValue("oldpin", "");
                    formik.setFieldValue("newpin", "");
                    formik.setFieldValue("cnewpin", "");
                    setShowPin.open();
                    setHeaderText(t("login.pin_expired"));
                    localStorage.setItem("token", res.token);
                } else if (res.success === "11") {
                    localStorage.setItem("token", res.token);
                    setShowLinkAcc.open();
                } else if (res.success === "01") {
                    triggerAlert(
                        "success",
                        "Login successful",
                        "You were succesfully logged in."
                    );
                } else {
                    setErrorToDisplay(
                        loginStatusMesages[res.success]
                            ? t(loginStatusMesages[res.success])
                            : res.message || "Please verify your credentials"
                    );
                }
            } catch (e) {
                console.log({e});
                setErrorToDisplay("Error occurred connecting to the server");
            }
        });
    };

    const initializePassword = async () => {
        setInitLoadin(true);
        const values = formik.values;
        const {pwd, ...data} = values;

        const payload = {
            etab: userData?.data?.etab,
            codewal: data?.codewal,
            oldpwd: hashString(values.oldPassword),
            newpwd: hashString(values.newPassword),
        };

        dispatch(authActions.handleUpdatePassword(payload)).then((action) => {
            try {
                setErrorToDisplay("");
                let res = action.payload;
                setShow.close();
                if (res.success === "01") {
                    triggerAlert(
                        "success",
                        "Password Initialisation successful",
                        "Password Initialisation successful. Glad to meet you back, login to continue"
                    );
                    // formik.values.pwd = "";
                    formik.resetForm();
                } else {
                    setErrorToDisplay("Could not initialise password");
                }
            } catch (error) {
                console.log({error});
            } finally {
                setInitLoadin(false);
            }
        });
    };

    const changePin = () => {
        setInitPinLoadin(true);
        const values = formik.values;
        const {pwd, ...data} = values;

        const payload = {
            etab: userData?.data?.etab,
            cli: userData?.data?.cli,
            codewal: data?.codewal,
            oldpin: hashString(values.oldpin),
            newpin: hashString(values.newpin),
        };

        dispatch(authActions.handleUpdatePin(payload)).then((action) => {
            let res = action.payload;
            setShowPin.close();

            setErrorToDisplay("");
            if (res?.success === "01") {
                setInitPinLoadin(false);
                triggerAlert(
                    "success",
                    "Pin Initialisation successful",
                    "Pin Initialisation successful. You can login to continue "
                );
            } else {
                setInitPinLoadin(false);
                setErrorToDisplay("Could not initialise pin");
            }

            formik.resetForm();
            setInitPinLoadin(false);
        });
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
    });

    const {values, handleBlur, handleChange, touched, errors, setFieldValue} =
        formik;

    const verifyOtp = (otp) => {
        const data = {
            tel: formik.values.codewal,
            etab: "001",
            codewal: formik.values.codewal,
            isLoginOtp: true,
            otp: hashString(otp),
        };

        setOtpState({loading: true});
        dispatch(authActions.handleVerifyOtp(data)).then((action) => {
            setOtpState({loading: false});
            if (isActionSuccess(action)) {
                try {
                    if (action.payload.success != "01") {
                        setOtpState(action?.payload);
                    }
                    if (action.payload.success === "01") {
                        window.location.replace("/overview");
                    }
                } catch (error) {
                    triggerAlert(
                        "failure",
                        t("error"),
                        t("an_error_occurred_please_try_again")
                    );
                }
            } else {
                triggerAlert(
                    "failure",
                    t("error"),
                    t("an_error_occurred_please_try_again")
                );
            }
        });
    };

    const attachAccount = () => {
        setProcessing(true);
        const values = formik.values;
        const {otp} = values;

        const payload = {
            tel: userData.data.tel,
            code: hashString(otp),
            cli: userData.data.cli,
            codewal: userData.data.codewal,
            etab: userData.data.etab,
        };

        dispatch(authActions.handleValidateAccAttachement(payload)).then(
            (action) => {
                setProcessing(false);
                let res = action.payload;
                setShowLinkAcc.close();

                if (res.success === "01") {
                    setErrorToDisplay("");
                    triggerAlert(
                        "success",
                        "successful",
                        "Operation successful. You can login to continue "
                    );
                } else {
                    setErrorToDisplay("Could not validate account attachement");
                }
            }
        );
    };

    const rejectAccountAtt = () => {
        setErrorToDisplay("");
        setProcessing(true);
        const values = formik.values;
        const {otp} = values;

        const payload = {
            tel: userData.data.tel,
            code: hashString(otp),
            cli: userData.data.cli,
            codewal: userData.data.codewal,
            etab: userData.data.etab,
        };

        dispatch(authActions.handleRejectAccAttachement(payload)).then((action) => {
            setProcessing(false);
            let res = action.payload;
            setShowLinkAcc.close();

            if (res.success === "01") {
                triggerAlert(
                    "success",
                    "successful",
                    "Operation successful. You can login to continue "
                );
            } else {
                setErrorToDisplay("Could not reject account attachement");
            }
        });
    };

    React.useEffect(() => {
        if (keyboardRef.current) {
            const keys = keyboardRef.current.querySelectorAll(".hg-button");
            keys.forEach((key) => {
                key.style.fontWeight = "bold";
            });
        }

        sessionStorage.clear();
    }, []);

    return (
        <div className="w-full md:grid md:grid-cols-2 lg:grid-cols-3 min-h-screen">
            <AuthImage/>
            <div className="flex items-center justify-center py-12  my-auto md:px-4 h-screen z-20 bg-white">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        formik.submitForm();
                    }}
                >
                    <div className="mx-auto w-full md:w-[30rem]">
                        <div className=" absolute top-5 right-10">
                            <LanguageSelector/>
                        </div>
                        <div
                            className={`${
                                errorToDisplay ? "opacity-100" : "opacity-0 pointer-events-none"
                            } bg-red-100 border border-red-200 rounded px-3 py-2 duration-100 mb-8 flex justify-between items-center`}
                        >
                            <span> {errorToDisplay}</span>
                            <span
                                className="cursor-pointer bg-red-500 rounded-full p-1"
                                onClick={() => setErrorToDisplay("")}
                            >
                <FaTimes color="white" size={12}/>{" "}
              </span>
                        </div>
                        <div className="flex justify-center items-center w-full">
                            <AppLogo/>
                        </div>
                        <div className="grid gap-2 text-center md:text-start mt-5">
                            <h1 className="text-3xl font-bold">{t("login.welcome_back")}</h1>
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-balance "
                            >
                                {t("login.login_description")}
                            </a>
                        </div>

                        <div className=" mt-4">
                            <div className="flex flex-col space-y-4 my-8 max-sm:px-4">
                                <Input
                                    label={t("login.wallet_id")}
                                    placeholder={t("login.wallet_id_placeholder")}
                                    name="codewal"
                                    value={values.codewal}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.codewal && errors.codewal}
                                />
                                <Input
                                    label={t("login.pin")}
                                    placeholder={t("login.pin_placeholder")}
                                    name="pwd"
                                    type="password"
                                    value={values.pwd}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        handleBlur(e);
                                    }}
                                    error={touched.pwd && errors.pwd}
                                />

                                <div className="flex md:justify-between max-sm:flex-col items-center ">
                                    <div>
                                        <div className="flex gap-x-3 items-center justify-start">
                                            <input
                                                checked={state.checked}
                                                onChange={(e) => {
                                                    setState({
                                                        ...state,
                                                        checked: e.target.checked,
                                                    });
                                                }}
                                                type="checkbox"
                                                className="text-primaryColor accent-primaryColor w-4 h-4 rounded"
                                            />
                                            <span>
                        {t("login.agree")}
                                                <a
                                                    href="https://www.bicec.com/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm font-semibold text-primaryColor underline cursor-pointer "
                                                >
                          {t("login.termsAndCond")}
                        </a>
                      </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-x-2 max-sm:mt-4">
                                        <Link
                                            className="underline font-semibold text-primaryColor"
                                            to={AUTH_ROUTES.Signup.path}
                                        >
                                            {t("login.SignUp")}
                                        </Link>
                                    </div>
                                </div>

                                <div
                                    className={`space-y-1 transition-all duration-1000 ease-in-out ${
                                        showKeyboard ? "hidden" : "block "
                                    }`}
                                >
                                    <p className="text-gray-500 text">
                                        {t("login.pin_description")}
                                    </p>
                                    <div className="flex justify-center ">
                                        <Keyboard
                                            ref={keyboardRef}
                                            layoutName={state.layoutName}
                                            onChange={(value) => setFieldValue("pwd", value)}
                                            onKeyPress={onKeyPress}
                                            display={{
                                                "{bksp}": "⌫",
                                                "{space}": "Space",
                                                "{shift}": "Shift",
                                                "{tab}": "⇥", // Tab symbol (Unicode character for Tab)
                                                "{lock}": "⇪", // Caps Lock symbol (Unicode character for Caps Lock)
                                                "{enter}": "⏎",
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="">
                                    <Button
                                        type="submit"
                                        className="w-full my-4"
                                        loading={loading}
                                    >
                                        {t("login.login")}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <OtpModal
                open={otpModal.open}
                onCancel={setOtpModal.close}
                formik={formik}
                loading={otpState.loading}
                error={otpState?.message1}
                onSubmit={verifyOtp}
            />
            <InitializePasswordModal
                isOpen={show.open}
                onClose={setShow.close}
                onSubmit={initializePassword}
                formik={formik}
                headerText={headerText}
                loading={initLoading}
            />
            <LinkAccModal
                isOpen={showLinkAcc.open}
                onClose={rejectAccountAtt}
                onSubmit={attachAccount}
                userData={userData}
                loading={processing}
                formik={formik}
            />
            <ChangePinModal
                isOpen={showPin.open}
                onClose={setShowPin.close}
                onSubmit={changePin}
                headerText={headerText}
                formik={formik}
                loading={initPinLoading}
            />
        </div>
    );
};

export default LoginScreen;